import PropTypes from 'prop-types';
import React from 'react';
import { withRouter } from 'react-router';
import { Enums } from '@letsdothis/tracking';

import classnames from 'classnames';
import { transformUrl } from '@utils/cloudinary';
import getUrl from '@/routes/routes/helpers/getUrl';
import { useTracking } from '@/tracking';

import { srOnly } from '@/base-styles/mixins.module.css';
import {
  clickableContainer,
  image,
  largeAvatar,
  largeBadge,
  mediumAvatar,
  mediumBadge,
  smallAvatar,
  smallBadge,
  initials as initialsStyle,
  outerWrapper,
  innerWrapper,
  initialsWrapper,
} from './styles.module.css';

const PICTURE_SIZE_SMALL = 50;
const PICTURE_SIZE_MEDIUM = 100;
const PICTURE_SIZE_LARGE = 150;

const getPictureSize = (avatarSize) => {
  if (avatarSize === smallAvatar) return PICTURE_SIZE_SMALL;
  if (avatarSize === mediumAvatar) return PICTURE_SIZE_MEDIUM;
  return PICTURE_SIZE_LARGE;
};

function getInitials(givenName, familyName, displayName) {
  let initials = '';

  if (givenName) {
    initials = givenName.charAt(0);
  }

  if (familyName) {
    initials += familyName.charAt(0);
  }

  if (!initials && displayName) {
    initials = displayName.charAt(0);
  }

  initials = initials.toUpperCase();
  return initials;
}

export const AvatarImage = ({
  className,
  fallbackImageUrl,
  user,
  avatarSize,
  useXLResolution,
}) => {
  const { givenName, familyName, displayName } = user;
  const initials = getInitials(givenName, familyName, displayName);

  const transformedUrl = transformUrl(user.pictureUrl, {
    fetchFormat: 'auto',
    quality: 'auto',
    crop: 'fit',
    dpr: 'auto',
    height: getPictureSize(avatarSize) * (useXLResolution ? 2 : 1),
  });

  const imageUrl = user.pictureUrl ? transformedUrl : fallbackImageUrl;
  return (
    <>
      <div className={initialsWrapper}>
        <p className={initialsStyle}>{initials}</p>
      </div>
      <div
        style={{
          backgroundImage: imageUrl ? `url(${imageUrl})` : 'transparent',
        }}
        className={classnames(className, image)}
      >
        <span className={srOnly}>{`${user.givenName || ''} ${user.familyName ||
          ''}`}</span>
      </div>
    </>
  );
};

/**
 * Avatar presentational component.
 */
const Avatar = (props) => {
  const {
    className,
    disableProfileClick,
    medium,
    small,
    user = {},
    fallbackImageUrl,
    avatarClassName,
    trackingSource,
    router,
    hideStaff,
    useXLResolution,
    shortNamePrefix,
  } = props;
  const { componentClicked } = useTracking();

  // TEMP: Disable LDT staff badges.
  const isStaff = false;
  const avatarSize = small ? smallAvatar : medium ? mediumAvatar : largeAvatar; // eslint-disable-line
  const badgeSize = small ? smallBadge : medium ? mediumBadge : largeBadge; // eslint-disable-line
  const { primaryIdentityId } = user;
  const disabledLink = disableProfileClick || !primaryIdentityId;

  function onClick() {
    if (disabledLink) {
      return;
    }
    const to = getUrl.publicProfileFromPrimaryIdentityId(primaryIdentityId);
    componentClicked({
      short_name: [shortNamePrefix, 'avatar'].filter(Boolean).join('_'),
      source: trackingSource,
      component_type: Enums.ComponentClickedComponentType.Link,
      component_link: to,
    });
    router.push(to);
  }

  return (
    <div className={classnames(avatarSize, className)}>
      <div
        className={classnames(outerWrapper, {
          [clickableContainer]: !disabledLink,
        })}
        onClick={onClick}
        onKeyPress={onClick}
      >
        <div className={classnames(innerWrapper, avatarClassName)}>
          <AvatarImage
            fallbackImageUrl={fallbackImageUrl}
            user={user}
            avatarSize={avatarSize}
            useXLResolution={useXLResolution}
          />
        </div>
      </div>
      {isStaff && !hideStaff && <span className={badgeSize} />}
    </div>
  );
};

Avatar.propTypes = {
  className: PropTypes.string,
  disableProfileClick: PropTypes.bool,
  avatarClassName: PropTypes.string,
  medium: PropTypes.bool,
  small: PropTypes.bool,
  user: PropTypes.shape({
    familyName: PropTypes.string,
    givenName: PropTypes.string,
    pictureUrl: PropTypes.string,
  }).isRequired,
  fallbackImageUrl: PropTypes.string,
  hideStaff: PropTypes.bool,
  shortNamePrefix: PropTypes.string,
  trackingSource: PropTypes.string,
};

Avatar.defaultProps = {
  className: '',
  disableProfileClick: false,
  avatarClassName: '',
  medium: false,
  small: false,
  fallbackImageUrl: undefined,
  hideStaff: false,
  shortNamePrefix: undefined,
  trackingSource: undefined,
};

export default withRouter(Avatar);
